import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { CalendarDays, CircleDollarSign, Receipt, RefreshCw, Repeat, Search, ShoppingBag, X } from "lucide-react";
import { getSales, getSubscriptions, syncSales } from "../../business/SalesBusiness";
import { updateUser } from "../../business/UserBusiness";
import {
  Avatar, Badge, Button, Card, CardTitle, ConfirmModal, EmptyState, Page, PageHeader, Segmented, Skeleton, SkeletonRows, Stat, inputClass,
} from "../../components/ui";
import {
  GATEWAY_STATUS, PROVIDERS, SALE_STATUS, SITUATIONS, money, planLabel, relativeDue, shortDate,
} from "../../constants/sales";

const TZ = "America/Sao_Paulo";
const DAY = 24 * 60 * 60 * 1000;
const brtDay = (date) => date.toLocaleDateString("en-CA", { timeZone: TZ });
const daysAgo = (n) => brtDay(new Date(Date.now() - n * DAY));

const PERIODS = [
  { value: "today", label: "Hoje" },
  { value: 7, label: "7 dias" },
  { value: 30, label: "30 dias" },
  { value: 90, label: "90 dias" },
  { value: 365, label: "12 meses" },
  { value: "custom", label: "Escolher" },
];

const rangeFor = (period) => (period === "today" ? [daysAgo(0), daysAgo(0)] : [daysAgo(period - 1), daysAgo(0)]);

const percentChange = (current, previous) => {
  if (!previous) return null;
  const change = Math.round(((current - previous) / previous) * 100);
  return `${change > 0 ? "+" : ""}${change}% vs. período anterior`;
};

const syncedAgo = (value) => {
  if (!value) return "Nunca sincronizado";
  const minutes = Math.round((Date.now() - new Date(value).getTime()) / 60000);
  if (minutes < 1) return "Atualizado agora";
  if (minutes < 60) return `Atualizado há ${minutes} min`;
  return `Atualizado às ${new Date(value).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", timeZone: TZ })}`;
};

const ProviderTag = ({ provider }) => (
  <span className="inline-flex items-center gap-1.5 text-sm text-neutral-700 dark:text-neutral-300">
    <span className={`size-2 rounded-full ${PROVIDERS[provider]?.dot || "bg-neutral-400"}`} />
    {PROVIDERS[provider]?.label || provider}
  </span>
);

// Colunas empilhadas por dia (ou semana, em períodos longos): Cakto embaixo, Hotmart em cima
const DailyChart = ({ byDay }) => {
  const [hover, setHover] = useState(null);

  const buckets = useMemo(() => {
    if (byDay.length <= 62) return byDay.map((d) => ({ ...d, label: d.date }));
    const weeks = [];
    for (let i = 0; i < byDay.length; i += 7) {
      const chunk = byDay.slice(i, i + 7);
      const sum = (p, k) => chunk.reduce((s, d) => s + d[p][k], 0);
      weeks.push({
        date: chunk[0].date,
        label: `${chunk[0].date} a ${chunk[chunk.length - 1].date}`,
        cakto: { count: sum("cakto", "count"), amount: sum("cakto", "amount") },
        hotmart: { count: sum("hotmart", "count"), amount: sum("hotmart", "amount") },
      });
    }
    return weeks;
  }, [byDay]);

  const max = Math.max(1, ...buckets.map((b) => b.cakto.count + b.hotmart.count));
  const fmt = (day) => new Date(`${day}T12:00:00-03:00`).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", timeZone: TZ });
  const weekly = byDay.length > 62;

  return (
    <div>
      <div className="flex items-center gap-4 text-xs text-neutral-500">
        {Object.entries(PROVIDERS).map(([key, p]) => (
          <span key={key} className="inline-flex items-center gap-1.5"><span className={`size-2 rounded-full ${p.dot}`} />{p.label}</span>
        ))}
        <span className="ml-auto">{weekly ? "por semana" : "por dia"} · máx. {max}</span>
      </div>
      <div className="relative mt-4 flex h-44 items-end gap-[2px] border-b border-neutral-200 dark:border-white/[0.08]" onMouseLeave={() => setHover(null)}>
        {buckets.map((b, i) => {
          const total = b.cakto.count + b.hotmart.count;
          return (
            <div key={b.date} className="group relative flex h-full min-w-0 flex-1 cursor-default flex-col justify-end" onMouseEnter={() => setHover(i)}>
              {hover === i && <div className="absolute inset-0 rounded-t bg-neutral-100 dark:bg-white/[0.04]" />}
              <div className="relative flex flex-col justify-end gap-[2px]" style={{ height: `${(total / max) * 100}%` }}>
                {b.hotmart.count > 0 && <div className={`rounded-t ${PROVIDERS.hotmart.dot}`} style={{ flexGrow: b.hotmart.count }} />}
                {b.cakto.count > 0 && <div className={`${b.hotmart.count ? "" : "rounded-t"} ${PROVIDERS.cakto.dot}`} style={{ flexGrow: b.cakto.count }} />}
              </div>
            </div>
          );
        })}
        {hover !== null && buckets[hover] && (
          <div
            className="pointer-events-none absolute bottom-full z-10 mb-2 w-48 -translate-x-1/2 rounded-xl border border-neutral-200 bg-white p-3 text-xs shadow-lg dark:border-white/[0.08] dark:bg-[#18181b]"
            style={{ left: `${Math.min(Math.max(((hover + 0.5) / buckets.length) * 100, 12), 88)}%` }}
          >
            <p className="font-medium text-neutral-900 dark:text-neutral-100">
              {weekly ? `Semana de ${fmt(buckets[hover].date)}` : fmt(buckets[hover].date)}
            </p>
            {Object.entries(PROVIDERS).map(([key, p]) => (
              <p key={key} className="mt-1.5 flex items-center gap-1.5 text-neutral-600 dark:text-neutral-300">
                <span className={`size-2 rounded-full ${p.dot}`} />{p.label}
                <span className="ml-auto">{buckets[hover][key].count} · {money(buckets[hover][key].amount)}</span>
              </p>
            ))}
          </div>
        )}
      </div>
      {buckets.length > 0 && (
        <div className="mt-2 flex justify-between text-xs text-neutral-400">
          <span>{fmt(buckets[0].date)}</span>
          <span>{fmt(buckets[buckets.length - 1].date)}</span>
        </div>
      )}
    </div>
  );
};

// Lista com barra proporcional (por gateway, por plano)
const Breakdown = ({ title, items, labelFor }) => {
  const max = Math.max(1, ...items.map((i) => i.amount));
  return (
    <Card className="p-5">
      <CardTitle>{title}</CardTitle>
      {items.length === 0 ? <p className="mt-4 text-sm text-neutral-400">Nenhuma venda no período</p> : (
        <ul className="mt-3 divide-y divide-neutral-100 dark:divide-white/[0.05]">
          {items.map((item) => (
            <li key={labelFor(item)} className="py-2.5">
              <div className="flex items-center gap-3 text-sm">
                <span className="flex-1 text-neutral-700 dark:text-neutral-300">{labelFor(item)}</span>
                <span className="text-xs text-neutral-400">{item.count} {item.count === 1 ? "venda" : "vendas"}</span>
                <b className="w-24 text-right font-medium text-neutral-900 dark:text-neutral-100">{money(item.amount)}</b>
              </div>
              <div className="mt-1.5 h-1 rounded-full bg-neutral-100 dark:bg-white/[0.05]">
                <div className="h-full rounded-full bg-emerald-500" style={{ width: `${(item.amount / max) * 100}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};

const SITUATION_ORDER = ["expired_active", "paid_inactive", "expiring", "active_no_payment", "no_account", "ok", "inactive"];

const SALE_FILTERS = [
  { value: "paid", label: "Pagas" },
  { value: "refunded", label: "Reembolsos" },
  { value: "other", label: "Pendentes e recusadas" },
  { value: "all", label: "Todas" },
];
const saleMatches = (filter, sale) =>
  filter === "all" ||
  (filter === "paid" && sale.status === "paid") ||
  (filter === "refunded" && ["refunded", "chargeback"].includes(sale.status)) ||
  (filter === "other" && !["paid", "refunded", "chargeback"].includes(sale.status));

export const Sales = () => {
  const [period, setPeriod] = useState(30);
  const [custom, setCustom] = useState(() => rangeFor(30));
  const [summary, setSummary] = useState(null);
  const [subs, setSubs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [situation, setSituation] = useState("expired_active");
  const [subSearch, setSubSearch] = useState("");
  const [saleFilter, setSaleFilter] = useState("paid");
  const [toggling, setToggling] = useState(null);
  const [saving, setSaving] = useState(false);

  const [from, to] = period === "custom" ? custom : rangeFor(period);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [s, subscriptions] = await Promise.all([getSales(from, to), getSubscriptions()]);
      setSummary(s);
      setSubs(subscriptions);
    } catch (err) {
      toast.error("Erro ao carregar as vendas");
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const handleSync = async () => {
    try {
      setSyncing(true);
      const { result } = await syncSales();
      const failed = Object.entries(result).filter(([, r]) => !r.ok).map(([p]) => PROVIDERS[p]?.label || p);
      if (failed.length) toast.error(`Não deu pra sincronizar: ${failed.join(", ")}`);
      else toast.success("Vendas atualizadas");
      await load();
    } catch (err) {
      toast.error("Erro ao sincronizar");
    } finally {
      setSyncing(false);
    }
  };

  // Ativar quem pagou e está inativo; desativar quem venceu e segue ativo
  const confirmToggle = async () => {
    const { user } = toggling;
    const next = user.subscription === "active" ? "inactive" : "active";
    try {
      setSaving(true);
      await updateUser(user._id, { subscription: next });
      toast.success(next === "active" ? "Assinatura ativada" : "Assinatura desativada");
      setToggling(null);
      setSubs(await getSubscriptions());
    } catch (err) {
      toast.error("Erro ao atualizar a assinatura");
    } finally {
      setSaving(false);
    }
  };

  const situationOptions = SITUATION_ORDER.map((key) => ({ value: key, label: SITUATIONS[key].label, count: subs?.counts?.[key] || 0 }));

  const subRows = useMemo(() => {
    const term = subSearch.toLowerCase();
    return (subs?.rows || [])
      .filter((r) => r.situation === situation)
      .filter((r) => !term || [r.user?.username, r.user?.email, r.gateway?.name, r.gateway?.email].some((v) => v?.toLowerCase().includes(term)))
      .sort((a, b) => new Date(a.gateway?.accessUntil || 0) - new Date(b.gateway?.accessUntil || 0));
  }, [subs, situation, subSearch]);

  const sales = useMemo(() => (summary?.sales || []).filter((s) => saleMatches(saleFilter, s)), [summary, saleFilter]);
  const period$ = summary?.period;

  return (
    <Page>
      <PageHeader title="Vendas" description="Cakto e Hotmart num lugar só, com o vencimento de cada assinatura.">
        <span className="text-xs text-neutral-500">{syncedAgo(summary?.lastSyncAt)}</span>
        <Button variant="secondary" icon={RefreshCw} loading={syncing} onClick={handleSync}>Sincronizar</Button>
      </PageHeader>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Segmented options={PERIODS} value={period} onChange={setPeriod} />
        {period === "custom" && (
          <div className="flex items-center gap-2">
            <input type="date" className={`${inputClass} w-auto`} value={custom[0]} max={custom[1]} onChange={(e) => setCustom([e.target.value, custom[1]])} />
            <span className="text-sm text-neutral-400">até</span>
            <input type="date" className={`${inputClass} w-auto`} value={custom[1]} min={custom[0]} max={daysAgo(0)} onChange={(e) => setCustom([custom[0], e.target.value])} />
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        <Stat label="Vendas hoje" value={summary?.today.count ?? 0} icon={CalendarDays} tone="green" loading={loading}
          hint={summary ? money(summary.today.amount) : null} />
        <Stat label="Vendas no período" value={period$?.count ?? 0} icon={ShoppingBag} tone="blue" loading={loading}
          hint={period$ ? `${period$.newCount} novas · ${period$.renewalCount} renovações` : null} />
        <Stat label="Faturamento no período" value={money(period$?.amount)} icon={CircleDollarSign} loading={loading}
          hint={period$ ? `${money(period$.net)} líquido${percentChange(period$.amount, summary.previous.amount) ? ` · ${percentChange(period$.amount, summary.previous.amount)}` : ""}` : null} />
        <Stat label="Assinantes pagando" value={subs?.paying ?? 0} icon={Repeat} tone="amber" loading={loading}
          hint={subs ? `${money(subs.mrr)} por mês (MRR)` : null} />
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <Card className="p-5 xl:col-span-2">
          <CardTitle right={period$ && period$.refunded.count > 0 && (
            <span className="text-xs text-red-500">{period$.refunded.count} reembolsadas · {money(period$.refunded.amount)}</span>
          )}>Vendas pagas</CardTitle>
          <div className="mt-4">{loading ? <Skeleton className="h-52 w-full" /> : <DailyChart byDay={summary?.byDay || []} />}</div>
        </Card>
        <div className="flex flex-col gap-4">
          {loading ? <Card className="p-5"><SkeletonRows rows={3} /></Card> : <>
            <Breakdown title="Por gateway" items={summary?.byProvider || []} labelFor={(i) => PROVIDERS[i.provider]?.label || i.provider} />
            <Breakdown title="Por plano" items={summary?.byPlan || []} labelFor={(i) => planLabel(i.plan)} />
          </>}
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="flex flex-col gap-3 border-b border-neutral-100 p-4 sm:p-5 dark:border-white/[0.05]">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-[15px] text-neutral-900 dark:text-neutral-100">Assinaturas</p>
              <p className="mt-0.5 text-xs text-neutral-500">{SITUATIONS[situation].hint}</p>
            </div>
            <div className="relative w-full lg:max-w-xs">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral-400" />
              <input value={subSearch} onChange={(e) => setSubSearch(e.target.value)} placeholder="Buscar por nome ou e-mail..." className={`${inputClass} pl-9 pr-8`} />
              {subSearch && <button onClick={() => setSubSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-neutral-400" aria-label="Limpar busca"><X className="size-4" /></button>}
            </div>
          </div>
          <Segmented options={situationOptions} value={situation} onChange={setSituation} />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-neutral-100 dark:border-white/[0.05]">
              <tr className="text-left text-xs font-medium text-neutral-500 dark:text-neutral-400">
                <th className="py-3 pl-5 pr-4 font-medium">Cliente</th>
                <th className="px-4 py-3 font-medium">Gateway</th>
                <th className="px-4 py-3 font-medium">Plano</th>
                <th className="hidden px-4 py-3 font-medium md:table-cell">No gateway</th>
                <th className="hidden px-4 py-3 font-medium md:table-cell">No Controlly</th>
                <th className="px-4 py-3 font-medium">Vencimento</th>
                <th className="hidden px-4 py-3 font-medium lg:table-cell">Último pagamento</th>
                <th className="w-px px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-white/[0.05]">
              {loading ? (
                <tr><td colSpan={8} className="p-5"><SkeletonRows rows={5} /></td></tr>
              ) : subRows.length === 0 ? (
                <tr><td colSpan={8}><EmptyState icon={Repeat} title="Ninguém nessa situação" description={subSearch ? "Tente outro nome ou e-mail." : null} /></td></tr>
              ) : subRows.map(({ user, gateway, situation: s }) => {
                const name = user?.username || gateway?.name || "—";
                const [statusLabel, statusTone] = GATEWAY_STATUS[gateway?.status] || [gateway?.status, "neutral"];
                const canToggle = user && (s === "expired_active" || s === "paid_inactive");
                return (
                  <tr key={user?._id || `${gateway.provider}:${gateway.externalId}`} className="transition-colors hover:bg-neutral-50 dark:hover:bg-white/[0.02]">
                    <td className="py-3 pl-5 pr-4">
                      <div className="flex items-center gap-3">
                        <Avatar name={name} />
                        <div className="min-w-0">
                          <p className="max-w-[200px] truncate text-sm text-neutral-900 dark:text-neutral-100">{name}</p>
                          <p className="max-w-[220px] truncate text-xs text-neutral-500">{user?.email || gateway?.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4">{gateway ? <ProviderTag provider={gateway.provider} /> : <span className="text-sm text-neutral-400">—</span>}</td>
                    <td className="whitespace-nowrap px-4 text-sm text-neutral-700 dark:text-neutral-300">{gateway ? planLabel(gateway.plan) : "—"}</td>
                    <td className="hidden whitespace-nowrap px-4 md:table-cell">{gateway ? <Badge tone={statusTone}>{statusLabel}</Badge> : <span className="text-sm text-neutral-400">—</span>}</td>
                    <td className="hidden whitespace-nowrap px-4 md:table-cell">
                      {user ? <Badge tone={user.subscription === "active" ? "green" : "neutral"}>{user.subscription === "active" ? "Ativa" : "Inativa"}</Badge> : <Badge tone="blue">Sem conta</Badge>}
                    </td>
                    <td className="whitespace-nowrap px-4">
                      <p className="text-sm text-neutral-900 dark:text-neutral-100">{shortDate(gateway?.accessUntil)}</p>
                      {gateway?.accessUntil && <p className={`text-xs ${new Date(gateway.accessUntil) < new Date() ? "text-red-500" : "text-neutral-500"}`}>{relativeDue(gateway.accessUntil)}</p>}
                    </td>
                    <td className="hidden whitespace-nowrap px-4 text-sm text-neutral-500 lg:table-cell">
                      {shortDate(gateway?.lastPaymentAt)}{gateway?.paidCount ? <span className="text-xs text-neutral-400"> · {gateway.paidCount}x</span> : null}
                    </td>
                    <td className="whitespace-nowrap px-4">
                      {canToggle && (
                        <Button variant="secondary" className="h-8 px-3 text-xs" onClick={() => setToggling({ user, situation: s })}>
                          {user.subscription === "active" ? "Desativar" : "Ativar"}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!loading && subRows.length > 0 && (
          <p className="border-t border-neutral-100 px-5 py-3 text-xs text-neutral-500 dark:border-white/[0.05]">{subRows.length} {subRows.length === 1 ? "conta" : "contas"}</p>
        )}
      </Card>

      <Card className="overflow-hidden">
        <div className="flex flex-col gap-3 border-b border-neutral-100 p-4 sm:p-5 lg:flex-row lg:items-center lg:justify-between dark:border-white/[0.05]">
          <p className="text-[15px] text-neutral-900 dark:text-neutral-100">Vendas do período</p>
          <Segmented options={SALE_FILTERS.map((f) => ({ ...f, count: (summary?.sales || []).filter((s) => saleMatches(f.value, s)).length }))} value={saleFilter} onChange={setSaleFilter} />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-neutral-100 dark:border-white/[0.05]">
              <tr className="text-left text-xs font-medium text-neutral-500 dark:text-neutral-400">
                <th className="py-3 pl-5 pr-4 font-medium">Data</th>
                <th className="px-4 py-3 font-medium">Cliente</th>
                <th className="px-4 py-3 font-medium">Gateway</th>
                <th className="hidden px-4 py-3 font-medium md:table-cell">Plano</th>
                <th className="hidden px-4 py-3 font-medium sm:table-cell">Tipo</th>
                <th className="px-4 py-3 text-right font-medium">Valor</th>
                <th className="hidden px-4 py-3 text-right font-medium lg:table-cell">Líquido</th>
                <th className="px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-white/[0.05]">
              {loading ? (
                <tr><td colSpan={8} className="p-5"><SkeletonRows rows={5} /></td></tr>
              ) : sales.length === 0 ? (
                <tr><td colSpan={8}><EmptyState icon={Receipt} title="Nenhuma venda aqui" description="Mude o período ou o filtro." /></td></tr>
              ) : sales.map((sale) => {
                const [label, tone] = SALE_STATUS[sale.status] || [sale.status, "neutral"];
                const when = sale.paidAt || sale.orderedAt;
                return (
                  <tr key={`${sale.provider}:${sale.externalId}`} className="text-sm">
                    <td className="whitespace-nowrap py-3 pl-5 pr-4 text-neutral-600 dark:text-neutral-300">
                      {when ? new Date(when).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: TZ }) : "—"}
                    </td>
                    <td className="px-4">
                      <p className="max-w-[200px] truncate text-neutral-900 dark:text-neutral-100">{sale.name || "—"}</p>
                      <p className="max-w-[220px] truncate text-xs text-neutral-500">{sale.email}</p>
                    </td>
                    <td className="whitespace-nowrap px-4"><ProviderTag provider={sale.provider} /></td>
                    <td className="hidden whitespace-nowrap px-4 text-neutral-700 md:table-cell dark:text-neutral-300">{planLabel(sale.plan)}</td>
                    <td className="hidden whitespace-nowrap px-4 text-neutral-500 sm:table-cell">{(sale.recurrence || 1) > 1 ? `Renovação ${sale.recurrence}ª` : "Nova"}</td>
                    <td className="whitespace-nowrap px-4 text-right text-neutral-900 dark:text-neutral-100">{money(sale.amount)}</td>
                    <td className="hidden whitespace-nowrap px-4 text-right text-neutral-500 lg:table-cell">{money(sale.net)}</td>
                    <td className="whitespace-nowrap px-4"><Badge tone={tone}>{label}</Badge></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {toggling && (
        <ConfirmModal
          title={toggling.user.subscription === "active" ? "Desativar assinatura" : "Ativar assinatura"}
          confirmLabel={toggling.user.subscription === "active" ? "Desativar" : "Ativar"}
          loading={saving}
          onConfirm={confirmToggle}
          onClose={() => setToggling(null)}
        >
          {toggling.user.subscription === "active"
            ? <>A Nath para de responder <b className="text-neutral-900 dark:text-neutral-100">{toggling.user.username}</b> ({toggling.user.email}) até um novo pagamento.</>
            : <>A conta de <b className="text-neutral-900 dark:text-neutral-100">{toggling.user.username}</b> ({toggling.user.email}) volta a ter acesso à Nath.</>}
        </ConfirmModal>
      )}
    </Page>
  );
};
